import { type FC, useEffect } from 'react';
import cn from 'classnames';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { RiCheckLine, RiErrorWarningLine } from 'react-icons/ri';
import { ImShuffle } from 'react-icons/im';
import Switch from '@/components/UI/FormElements/Switch';
import { TeamThemeService } from '@/support/TeamThemeService';
import type { Color } from '@/model/Common.model';
import ColorInput from './ColorInput';
import {
  type TeamConfigFormValues,
  isValidHexColor,
  wcagContrastRatio,
  passesWcagAA,
  generateRandomTheme,
} from './TeamConfig.utils';
import styles from './TeamConfig.module.scss';

const THEME_PRESETS = [
  { label: 'Production', primary: '#000db5', onPrimary: '#ffffff' },
  { label: 'Test', primary: '#ff922b', onPrimary: '#000000' },
  { label: 'Dev', primary: '#00823d', onPrimary: '#ffffff' },
] as const;

interface ThemeCardProps {
  testId?: string;
}

/**
 * Config card for managing the tenant sidebar theme.
 * Shows color pickers for primary/on_primary with live sidebar preview and WCAG contrast checking.
 * When inactive, the form is visible but disabled.
 */
const ThemeCard: FC<ThemeCardProps> = ({ testId = 'theme-card' }) => {
  const { control, setValue } = useFormContext<TeamConfigFormValues>();
  const active = useWatch({ control, name: 'theme.active' });
  const primary = useWatch({ control, name: 'theme.value.primary' });
  const onPrimary = useWatch({ control, name: 'theme.value.on_primary' });

  const bothValid = isValidHexColor(primary) && isValidHexColor(onPrimary);

  useEffect(() => {
    if (active && bothValid) {
      TeamThemeService.preview({ primary: primary as Color, on_primary: onPrimary as Color });
    } else {
      TeamThemeService.applyFromCache();
    }
  }, [active, primary, onPrimary, bothValid]);

  useEffect(() => {
    return () => {
      TeamThemeService.applyFromCache();
    };
  }, []);

  const contrastRatio = bothValid ? wcagContrastRatio(primary, onPrimary) : null;
  const contrastLevel = contrastRatio === null ? 'disabled' : passesWcagAA(contrastRatio) ? 'pass' : 'fail';

  return (
    <div className={styles.card} data-testid={testId}>
      <div className={styles.cardHeader}>
        <div>
          <span className="fw6 f5">Theme</span>
          <p className="f6 dark-gray lh-copy mt2 mb0">
            Controls the appearance of the navigation sidebar across this tenant. <strong>primary</strong> sets the
            sidebar background; <strong>on_primary</strong> sets the icon and text colour.
          </p>
        </div>
        <div className={`flex items-center ${styles.headerActions}`}>
          <span className="f6 mid-gray">{active ? 'Active' : 'Inactive'}</span>
          <Controller
            name="theme.active"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Switch value={value} onChange={onChange} testId={`${testId}-active-switch`} />
            )}
          />
        </div>
      </div>

      <div className={cn(styles.cardBody, { [styles.cardBodyDisabled]: !active })} data-testid={`${testId}-body`}>
        <div className="mb3" data-testid={`${testId}-presets`}>
          <span className="f6 fw6 db mb2">Defaults</span>
          <div className={`flex items-center ${styles.presetRow}`}>
            {THEME_PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                disabled={!active}
                className={styles.presetButton}
                style={{ background: preset.primary, color: preset.onPrimary }}
                onClick={() => {
                  setValue('theme.value.primary', preset.primary, { shouldDirty: true, shouldValidate: true });
                  setValue('theme.value.on_primary', preset.onPrimary, { shouldDirty: true, shouldValidate: true });
                }}
                data-testid={`${testId}-preset-${preset.label.toLowerCase()}`}
              >
                {preset.label}
              </button>
            ))}
            <button
              type="button"
              disabled={!active}
              className={styles.presetButtonRound}
              style={{ background: '#e9ecef', color: '#495057' }}
              onClick={() => {
                const { primary: p, onPrimary: op } = generateRandomTheme();
                setValue('theme.value.primary', p, { shouldDirty: true, shouldValidate: true });
                setValue('theme.value.on_primary', op, { shouldDirty: true, shouldValidate: true });
              }}
              data-testid={`${testId}-preset-random`}
            >
              <ImShuffle size={16} />
            </button>
          </div>
        </div>

        <div className={`flex items-end ${styles.colorFields}`}>
          <Controller
            name="theme.value.primary"
            control={control}
            rules={{ validate: isValidHexColor }}
            render={({ field: { onChange, value }, fieldState: { invalid } }) => (
              <ColorInput
                value={value}
                onChange={onChange}
                fieldName="primary"
                label="Main brand color"
                error={invalid}
                disabled={!active}
                testId={`${testId}-primary`}
              />
            )}
          />
          <Controller
            name="theme.value.on_primary"
            control={control}
            rules={{ validate: isValidHexColor }}
            render={({ field: { onChange, value }, fieldState: { invalid } }) => (
              <ColorInput
                value={value}
                onChange={onChange}
                fieldName="on_primary"
                label="Content color on primary"
                error={invalid}
                disabled={!active}
                testId={`${testId}-on-primary`}
              />
            )}
          />
        </div>

        <div
          className={cn(styles.contrastBadge, styles[`contrast_${contrastLevel}`], 'mt3')}
          data-testid={`${testId}-contrast-badge`}
        >
          {contrastRatio === null ? (
            <>— Contrast unavailable</>
          ) : (
            <>
              Contrast {contrastRatio.toFixed(1)}:1{' '}
              {contrastLevel === 'pass' ? (
                <RiCheckLine size={14} className="green" />
              ) : (
                <RiErrorWarningLine size={14} className="red" />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThemeCard;
